import { inject, Injectable, signal } from '@angular/core'
import { firstValueFrom, forkJoin, map, Observable } from 'rxjs'
import { EntriesService, EntriesShowExternal, ExternalFormResponse, ExternalFormsService } from 'superlikers-microsites-api'

import { Notification, NotificationStatus } from '@notifications/models/notifications'
import { Participant } from '@participant/models/participant'

@Injectable({
  providedIn: 'root'
})
export class NotificationsService {
  externalsFormsService = inject(ExternalFormsService)
  entriesService = inject(EntriesService)

  #notificationsSignal = signal<Notification[]>([])
  notifications = this.#notificationsSignal.asReadonly()

  #notificationDropdownSignal = signal<boolean>(false)
  notificationDropdown = this.#notificationDropdownSignal.asReadonly()

  constructor() {
    this.getNotifications()
  }

  async getNotifications() {
    const CATEGORY = 'notifications'

    const list$ = this.externalsFormsService.getExternalsFormList({ page: 1, category: CATEGORY }).pipe(map(response => response.data))
    const list = await firstValueFrom(list$)

    const fieldsWrapper$ = list.map(item => this.externalsFormsService.getExternalsFormFields(item._id))
    const fields = await firstValueFrom(forkJoin(fieldsWrapper$))

    const externalForms$ = this.externalsFormsService.getExternalsForms<Participant, Partial<Notification>>({ category: CATEGORY }).pipe(map(response => response.data))
    const externalForms = await firstValueFrom(externalForms$)

    const data = fields.map(item => {
      const itemFields = item.object.fields
      const notification = Object.fromEntries(itemFields.map(field => [field.name, field.default_value])) as unknown as Notification
      notification.form_id = item.object.id

      const external = externalForms.find(e => e.atype === item.object.external_action) as EntriesShowExternal<Participant, Partial<Notification>>
      if (!external) return null

      if (external && external.data) {
        notification.status = external.data.status as NotificationStatus
        notification.id = external._id
      }

      return notification
    })

    const notifications = data.filter(Boolean) as Notification[]
    this.#notificationsSignal.set(notifications)
  }

  async markAsRead(notification: Notification) {
    const { id, form_id, ...rest } = notification

    if (rest.status === 'read') return
    rest.status = 'read'

    let formResponse$!: Observable<ExternalFormResponse>

    if (!id) {
      formResponse$ = this.externalsFormsService.createExternalForm(form_id, rest)
    } else {
      formResponse$ = this.externalsFormsService.updateExternalForm(id, rest)
    }

    const formResponse = await firstValueFrom(formResponse$)

    if (formResponse.state === 'success') {
      const updatedNotification = { id, ...rest } as Notification

      const externalResponse$ = this.entriesService.sendExternal({ event: 'notifications', properties: rest, category: rest.category })
      await firstValueFrom(externalResponse$)

      this.updateNotifications(updatedNotification)
    }
  }

  private updateNotifications(notification: Notification) {
    const notifications = this.notifications()

    const updatedNotifications = notifications.map(n => {
      return notification.id === n.id ? notification : n
    })

    this.#notificationsSignal.set(updatedNotifications)
  }

  toggleDropdown() {
    this.#notificationDropdownSignal.update(value => !value)
  }

  closeDropdown() {
    this.#notificationDropdownSignal.set(false)
  }
}
